// src/components/AssignedEmployees.js

import React, { useEffect, useState } from 'react';
import { db } from '../../firebaseConfig';
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  arrayRemove
} from 'firebase/firestore';

import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';

// Importiere den TenantContext
import { useTenant } from '../../contexts/TenantContext';

const AssignedEmployees = () => {
  const [types, setTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  // tenantId aus Context
  const { tenantId } = useTenant();

  useEffect(() => {
    if (!tenantId) return; // Warte, bis tenantId vorliegt
  
    // Listener für appointmentTypes
    const typesRef = collection(db, 'tenants', tenantId, 'appointmentTypes');
    const unsubscribeTypes = onSnapshot(typesRef, (snapshot) => {
      const typesList = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      setTypes(typesList);
    }, (error) => {
      console.error('Fehler beim Abrufen der Terminarten:', error);
      setError('Fehler beim Abrufen der Terminarten.');
    });
  
    // Listener für employees
    const employeesRef = collection(db, 'tenants', tenantId, 'employees');
    const unsubscribeEmployees = onSnapshot(employeesRef, (snapshot) => {
      const employeesList = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      setEmployees(employeesList);
    }, (error) => {
      console.error('Fehler beim Abrufen der Mitarbeiter:', error);
      setError('Fehler beim Abrufen der Mitarbeiter.');
    });
  
    setLoading(false);
  
    // Cleanup: Listener entfernen, wenn Komponente unmountet wird
    return () => {
      unsubscribeTypes();
      unsubscribeEmployees();
    };
  }, [tenantId]);
  
  // ^ tenantId im Dependency-Array: neu abfragen, falls tenantId sich ändert

  // Hilfsfunktion: Mitarbeitername ermitteln
  const getEmployeeName = (id) => {
    const emp = employees.find(e => e.id === id);
    return emp ? emp.name : 'Unbekannt';
  };

  // Mitarbeiter-Zuordnung entfernen
  const handleRemove = async (typeId, employeeId) => {
    if (window.confirm('Möchten Sie diese Zuordnung wirklich entfernen?')) {
      try {
        // Richtig: doc(db, 'tenants', tenantId, 'appointmentTypes', typeId)
        const typeDocRef = doc(db, 'tenants', tenantId, 'appointmentTypes', typeId);

        await updateDoc(typeDocRef, {
          assignedEmployees: arrayRemove(employeeId)
        });

        // Lokalen State aktualisieren
        setTypes(types.map(type =>
          type.id === typeId
            ? {
                ...type,
                assignedEmployees: type.assignedEmployees.filter(id => id !== employeeId)
              }
            : type
        ));

        setMessage('Mitarbeiter erfolgreich entfernt.');
        setError(null);
      } catch (err) {
        console.error(err);
        setError('Fehler beim Entfernen der Zuordnung.');
      }
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      {loading && (
        <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}

      {!loading && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Terminart</TableCell>
                <TableCell>Zugeordnete Mitarbeiter</TableCell>
                <TableCell>Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
  {types.map((type) => (
    <TableRow key={type.id}>
      {/* Spalte: Terminart */}
      <TableCell>{type.name}</TableCell>

      {/* Spalte: Zugeordnete Mitarbeiter */}
      <TableCell>
        {type.assignedEmployees && type.assignedEmployees.length > 0 ? (
          type.assignedEmployees.map((empId) => (
            <Typography key={empId}>{getEmployeeName(empId)}</Typography>
          ))
        ) : (
          <Typography>Keine Mitarbeiter zugeordnet</Typography>
        )}
      </TableCell>

      {/* Spalte: Aktionen */}
      <TableCell>
        {type.assignedEmployees && type.assignedEmployees.length > 0 ? (
          type.assignedEmployees.map((empId) => (
            <Box
              key={empId}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
            >
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() => handleRemove(type.id, empId)}
              >
                Entfernen
              </Button>
            </Box>
          ))
        ) : (
          <></>
        )}
      </TableCell>
    </TableRow>
  ))}
</TableBody>

          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AssignedEmployees;
